import { useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { Colors, Shapes, themeSpacing } from 'src/components/theme'
import { UnavailableCardContent } from 'src/components/insights/unavailable-card-content'
import { FC, useMemo } from 'react'
import { PermittedLandUsesKey, ZoneDetailResponse } from 'src/common-services/types/zone-types'

import * as atoms from 'src/atoms/zone'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
  padding-inline: ${Shapes.spacing[4]}px;
`

type ZoningUsesListsProps = {
  type: PermittedLandUsesKey
}

export const ZoningUsesLists: FC<ZoningUsesListsProps> = (props) => {
  const { type } = props
  const details = useRecoilValue(atoms.zoningDetailsForGooglePlace)
  const search = useRecoilValue(atoms.zoningUsesSearch)

  if (!details) {
    return <UnavailableCardContent message={`No Zoning data Available`} />
  }

  return <ZoningUsesListsContent details={details} search={search} type={type} />
}

type ZoningUsesListsContentProps = {
  details?: ZoneDetailResponse
  type: PermittedLandUsesKey
  search?: string
}
export const ZoningUsesListsContent: FC<ZoningUsesListsContentProps> = ({ details, type, search }) => {
  if (!details || !details.permitted_land_uses || !details.permitted_land_uses[type]) {
    return <UnavailableCardContent message="No Zoning Uses Available" />
  }

  return (
    <Wrapper>
      <ZoneUsesLists details={details} type={type} search={search} />
    </Wrapper>
  )
}

const ZoneUsesList = styled.ul`
  flex: 1;
  margin: 0;
  padding: 0;
  padding-left: ${themeSpacing(5)}px;
`

const ZoneUsesItem = styled.li`
  color: ${Colors.textMedium};
  margin-block: ${themeSpacing(2)}px;
  word-wrap: break-word;
  &:first-of-type {
    margin-top: ${themeSpacing(0)}px;
  }
`

const NoZoneResults = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${themeSpacing(4)}px;
`

const ZoneUsesListsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px ${Colors.border} solid;
  &:last-of-type {
    border-bottom: none;
  }
`

const ZoneUsesContainer = styled.div`
  display: flex;
  gap: ${themeSpacing(6)}px;
  align-items: start;
  list-style-position: outside;
`

export const chunkArray = (array: any[], totalChunks: number) => {
  const chunkSize = Math.ceil(array.length / totalChunks)
  const result: any[] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize))
  }
  return result
}

const ZoneUsesLists: React.FC<{
  details: ZoneDetailResponse
  type: PermittedLandUsesKey
  search?: string
}> = (props) => {
  const { details, type, search } = props

  const chunked = useMemo<Array<Array<string>>>(() => {
    if (!!!details.permitted_land_uses || !details.permitted_land_uses[type]) {
      return []
    }
    const items = details.permitted_land_uses[type] as string[]
    const filteredItems = items.filter((item) => item.toLowerCase().includes(search?.toLowerCase() || '')) || []
    return chunkArray(filteredItems, 3)
  }, [details, search])

  return (
    <ZoneUsesListsWrapper>
      <ZoneUsesContainer>
        {!details || !details.permitted_land_uses || !details.permitted_land_uses[type] || chunked.length === 0 ? (
          <NoZoneResults>
            <Typography color={Colors.textLight}>No results found</Typography>
          </NoZoneResults>
        ) : (
          <>
            {chunked.map((chunk, index) => (
              <ZoneUsesList key={index}>
                {chunk.map((item) => (
                  <ZoneUsesItem key={item}>{item}</ZoneUsesItem>
                ))}
              </ZoneUsesList>
            ))}
          </>
        )}
      </ZoneUsesContainer>
    </ZoneUsesListsWrapper>
  )
}
